/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Popup, Placeholder, Icon } from 'semantic-ui-react';

import { isStoreError, isStoreReady } from '@amzn/base-ui/dist/models/BaseStore';
import { swallowError, nicePrice } from '@amzn/base-ui/dist/helpers/utils';

// Icon style fix for alignment
const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// expected props
// - envId (via prop)
// - compact (via prop) - optional, for compact display mode
// - scEnvironmentCostsStore  (via injection)
class ScEnvironmentCost extends React.Component {
  componentDidMount() {
    const costStore = this.getEnvCostStore();
    if (!isStoreReady(costStore) && !isStoreError(costStore)) {
      swallowError(costStore.load());
    }
  }

  get costsStore() {
    return this.props.scEnvironmentCostsStore;
  }

  get envId() {
    return this.props.envId;
  }

  get envCost() {
    const envId = this.envId;
    return this.costsStore.getScEnvironmentCost(envId);
  }

  get compact() {
    return this.props.compact || false;
  }

  get effectiveCpuUtilization() {
    const { cpuUtilization, cpuUtilizationUpdatedAt } = this.props;
    if (cpuUtilization == null) return null;
    if (cpuUtilizationUpdatedAt) {
      const updatedAt = new Date(cpuUtilizationUpdatedAt).getTime();
      const now = Date.now();
      const twentyFourHours = 24 * 60 * 60 * 1000;
      if (now - updatedAt > twentyFourHours) return null;
    }
    return cpuUtilization;
  }

  getEnvCostStore() {
    const costsStore = this.costsStore;
    const envId = this.envId;
    return costsStore.getScEnvironmentCostStore(envId);
  }

  render() {
    if (this.compact) {
      return this.renderCompact();
    }
    return this.renderDefault();
  }

  renderCompact() {
    const envCost = this.envCost;
    const isLoading = _.isUndefined(envCost);
    const isError = !isLoading && !_.isEmpty(envCost.error);

    if (isLoading) {
      return (
        <div>
          <Placeholder style={{ width: '60px', margin: '0 auto' }}>
            <Placeholder.Line length="full" />
          </Placeholder>
        </div>
      );
    }

    if (isError) {
      return (
        <Popup
          trigger={
            <div style={{ textAlign: 'center', color: '#dc2626', fontSize: '0.85em' }}>
              <Icon name="exclamation triangle" />
              <div>Cost unavailable</div>
            </div>
          }
          content={
            <div style={{ maxWidth: '250px' }}>
              <p>Error retrieving cost information.</p>
              <p style={{ fontSize: '0.85em', color: '#666' }}>{envCost.error}</p>
            </div>
          }
          position="top center"
          size="small"
        />
      );
    }

    const previous30Cost = envCost.get30DayAmountByEnv;
    const totalYTDCostByEnv = envCost.getYTDAmountByEnv;

    return (
      <div>
        <div className="workspace-info-card-label">
          <Icon name="tachometer alternate" style={iconStyle} />
          Usage
        </div>
        <div>
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">Credits (30d)</span>
            <span className="workspace-info-card-row-value">{nicePrice(previous30Cost)}</span>
          </div>
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">Credits (YTD)</span>
            <span className="workspace-info-card-row-value">{nicePrice(totalYTDCostByEnv)}</span>
          </div>
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">CPU Utilisation (24h)</span>
            <span className="workspace-info-card-row-value" style={this.effectiveCpuUtilization != null ? { color: this.effectiveCpuUtilization <= 10 ? '#dc2626' : this.effectiveCpuUtilization <= 25 ? '#d97706' : this.effectiveCpuUtilization <= 90 ? '#16a34a' : this.effectiveCpuUtilization <= 95 ? '#d97706' : '#dc2626' } : { color: '#9ca3af' }}>
              {this.effectiveCpuUtilization != null ? `${this.effectiveCpuUtilization}%` : '--'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  renderDefault() {
    const envCost = this.envCost;
    const isLoading = _.isUndefined(envCost);
    const isError = !isLoading && !_.isEmpty(envCost.error);

    if (isLoading) {
      return (
        <div className="workspace-cost-card workspace-cost-card-detail">
          <Placeholder>
            <Placeholder.Line length="full" />
            <Placeholder.Line length="full" />
            <Placeholder.Line length="medium" />
          </Placeholder>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="workspace-cost-card workspace-cost-card-detail">
          <Popup
            trigger={
              <div style={{ textAlign: 'center' }}>
                <Icon name="exclamation triangle" style={{ ...iconStyle, color: '#dc2626', fontSize: '1.5em' }} />
                <div style={{ marginTop: '8px', color: '#dc2626', fontWeight: '600' }}>Cost Unavailable</div>
                <div style={{ marginTop: '4px', fontSize: '0.85em', color: '#666' }}>Click for details</div>
              </div>
            }
            content={
              <div style={{ maxWidth: '300px' }}>
                <p><strong>Error retrieving cost information.</strong></p>
                <p>If this workspace is provisioned in a new account, verify with your administrator that the cost explorer feature is enabled for the account.</p>
                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '8px' }}>{envCost.error}</p>
              </div>
            }
            position="top center"
            size="small"
          />
        </div>
      );
    }

    const totalYTDCostByEnv = envCost.getYTDAmountByEnv;
    const previous30Cost = envCost.get30DayAmountByEnv;

    return (
      <div className="workspace-cost-card workspace-cost-card-detail">
        <div className="workspace-cost-label-detail">
          <Icon name="dollar sign" style={iconStyle} />
          Credit Usage
        </div>
        
        <div className="workspace-cost-main">
          <div className="workspace-cost-value-detail">{nicePrice(previous30Cost)}</div>
          <div className="workspace-cost-period-detail">30 Day Cost</div>
        </div>
        
        <div className="workspace-cost-ytd-detail">
          <div className="workspace-cost-ytd-row">
            <span className="workspace-cost-ytd-value-detail">{nicePrice(totalYTDCostByEnv)}</span>
            <span className="workspace-cost-ytd-label-detail">{new Date().getFullYear()} YTD</span>
          </div>
        </div>
      </div>
    );
  }


}

decorate(ScEnvironmentCost, {
  costsStore: computed,
  envId: computed,
  compact: computed,
  effectiveCpuUtilization: computed,
});

export default inject('scEnvironmentCostsStore')(withRouter(observer(ScEnvironmentCost)));
